'use client';

import { useState, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card.tsx';
import { Button } from '../components/ui/button.tsx';
import { ChevronDown, ChartPie, BarChart, Users } from 'lucide-react';
import { DepartmentEditPanel } from '../components/sessions/DepartmentEditPanel';
import { AddDepartmentModal } from '../components/sessions/AddDepartmentModal';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog.tsx';
import {
  useGetDepartments,
  useDeleteDepartment,
} from '../api/department/departmentQueries';
import { generateDepartmentColor } from '../utils/constants';
import useAuth from '../hooks/useAuth';
import { toast } from 'react-toastify';
import DepartmentCards from '../components/dashboard/DepartmentCards';

// Calculate department performance based on metrics
const calculatePerformance = (department) => {
  const maxStudents = 100; // baseline for percentage calculation
  const maxStaff = 20;

  // Handle missing or invalid data
  const studentCount = Math.max(0, department?.studentCount || 0);
  const staffCount = Math.max(0, department?.staffCount || 0);

  // Return default values if no meaningful data exists
  if (studentCount === 0 && staffCount === 0) {
    return {
      studentPerformance: 0,
      staffPerformance: 0,
      totalScore: 0,
    };
  }

  const studentScore = (studentCount / maxStudents) * 100;
  const staffScore = (staffCount / maxStaff) * 100;

  const totalScore = (studentScore + staffScore) / 2;

  return {
    studentPerformance: Math.min(Math.round(studentScore), 100),
    staffPerformance: Math.min(Math.round(staffScore), 100),
    totalScore: Math.round(totalScore),
  };
};

export default function DepartmentDashboard() {
  const navigate = useNavigate();
  const { sessionId } = useParams();
  const [isAddDepartmentOpen, setIsAddDepartmentOpen] = useState(false);
  const [selectedDepartment, setSelectedDepartment] = useState(null);
  const [isDepartmentEditOpen, setIsDepartmentEditOpen] = useState(false);
  const [departmentToDelete, setDepartmentToDelete] = useState(null);
  const { user } = useAuth();

  const { data: departmentsData = [], isLoading: departmentsLoading } =
    useGetDepartments(user?.organizationId);
  const { mutate: deleteDepartment } = useDeleteDepartment();

  // Transform departments data to ensure consistent format
  const departments = useMemo(() => {
    if (!departmentsData) return [];
    const data = Array.isArray(departmentsData)
      ? departmentsData
      : departmentsData.data && Array.isArray(departmentsData.data)
      ? departmentsData.data
      : departmentsData.departments &&
        Array.isArray(departmentsData.departments)
      ? departmentsData.departments
      : [];

    // Ensure each department has consistent ID field
    return data.map((dept) => ({
      ...dept,
      deptId: dept.id || dept.deptId,
    }));
  }, [departmentsData]);

  const overallPerformance = useMemo(() => {
    if (!departments.length) return 0;

    const validDepartments = departments.filter((dept) => {
      const performance = calculatePerformance(dept);
      return performance.totalScore > 0;
    });

    if (!validDepartments.length) return 0;

    const totalPerf = validDepartments.reduce((acc, dept) => {
      const { totalScore } = calculatePerformance(dept);
      return acc + totalScore;
    }, 0);

    return Math.round(totalPerf / validDepartments.length);
  }, [departments]);

  const departmentColors = useMemo(() => {
    const colorMap = new Map();
    departments.forEach((dept) => {
      if (!colorMap.has(dept.deptId)) {
        colorMap.set(dept.deptId, generateDepartmentColor());
      }
    });
    return colorMap;
  }, [departments]);

  const handleViewDepartment = (department) => {
    const deptId = department?.id || department?.deptId;
    if (!deptId) {
      toast.error('Invalid department ID');
      return;
    }
    navigate(`/departments/${deptId}`);
  };

  const handleEditDepartment = (department) => {
    const deptId = department?.id || department?.deptId;
    if (!deptId) {
      toast.error('Invalid department ID');
      return;
    }
    setSelectedDepartment(department);
    setIsDepartmentEditOpen(true);
  };

  const handleDeleteDepartment = (department) => {
    const deptId = department?.id || department?.deptId;
    if (!deptId) {
      toast.error('Invalid department ID');
      return;
    }
    setDepartmentToDelete(department);
  };

  const confirmDeleteDepartment = async () => {
    if (!departmentToDelete) return;

    const deptId = departmentToDelete?.id || departmentToDelete?.deptId;
    if (!deptId) {
      toast.error('Invalid department ID');
      return;
    }

    try {
      await deleteDepartment(deptId);
      setDepartmentToDelete(null);
    } catch (error) {
      toast.error('Failed to delete department');
      console.error('Delete department error:', error);
    }
  };

  const renderPerformanceChart = (department) => {
    const { studentPerformance, staffPerformance } =
      calculatePerformance(department);
    const { bg, text } =
      departmentColors.get(department.deptId) || generateDepartmentColor();

    return (
      <div
        key={department.deptId}
        className="flex flex-col items-center gap-2 w-1/6"
      >
        <div className="relative w-full h-full">
          {staffPerformance > 0 && (
            <div
              className="absolute bottom-0 w-full rounded-md transition-all duration-300"
              style={{
                height: `${staffPerformance}%`,
                backgroundColor: bg,
                opacity: 0.5,
              }}
            />
          )}
          {studentPerformance > 0 && (
            <div
              className="absolute bottom-0 w-full rounded-md transition-all duration-300"
              style={{
                height: `${studentPerformance}%`,
                backgroundColor: text,
                opacity: 0.8,
              }}
            />
          )}
          {studentPerformance === 0 && staffPerformance === 0 && (
            <div className="absolute bottom-0 w-full h-4 rounded-md bg-gray-100" />
          )}
        </div>
        <span className="text-xs text-gray-500">
          {department.name || 'Unnamed'}
        </span>
      </div>
    );
  };

  const renderDepartmentStats = (department) => {
    const { bg, text } =
      departmentColors.get(department.deptId) || generateDepartmentColor();
    const totalMembers =
      (department.studentCount || 0) + (department.staffCount || 0);
    const prevTotal = totalMembers * 0.8; // Simulated previous term data

    // Calculate growth rate safely
    const growth =
      prevTotal > 0 ? ((totalMembers - prevTotal) / prevTotal) * 100 : 0;

    return (
      <div key={department.deptId} className="space-y-2">
        <div className="flex items-center gap-2">
          <div
            className="w-2 h-2 rounded-full"
            style={{ backgroundColor: text }}
          ></div>
          <span className="text-sm text-gray-500">
            {department.name || 'Unnamed Department'}
          </span>
        </div>
        <div className="flex items-baseline gap-2">
          <span className="text-2xl font-semibold">{totalMembers}</span>
          {totalMembers > 0 && (
            <span
              className={`text-xs ${
                growth >= 0 ? 'text-green-500' : 'text-red-500'
              }`}
            >
              {growth >= 0 ? '+' : ''}
              {growth.toFixed(1)}%
            </span>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="w-full p-4 space-y-4">
      {/* Top section with departments and gauge */}
      <div className="flex flex-col md:flex-row gap-4">
        {/* Active Departments Card */}
        <Card className="w-full">
          <CardContent className="p-4">
            <DepartmentCards
              departments={departments}
              onAddDepartment={() => setIsAddDepartmentOpen(true)}
              onViewDepartment={handleViewDepartment}
              onEditDepartment={handleEditDepartment}
              onDeleteDepartment={handleDeleteDepartment}
            />
          </CardContent>
        </Card>

        {/* Gauge Chart Card */}
        <Card className="w-full md:flex-1">
          <CardContent className="p-4 h-full">
            <div className="text-lg font-medium mb-4">Overall Performance</div>
            <div className="flex flex-col items-center justify-center h-[calc(100%-3rem)]">
              {departments.length > 0 ? (
                <div className="relative w-full max-w-xs h-40">
                  <svg className="w-full h-full" viewBox="0 0 100 50">
                    {/* Background track */}
                    <path
                      d="M 10,50 A 40,40 0 0 1 90,50"
                      stroke="#f3f4f6"
                      strokeWidth="10"
                      fillOpacity="0"
                    />
                    {/* Colored progress */}
                    <path
                      d="M 10,50 A 40,40 0 0 1 90,50"
                      stroke="#8b5cf6"
                      strokeWidth="10"
                      fillOpacity="0"
                      strokeDasharray={`${
                        (overallPerformance / 100) * 125.6
                      } 125.6`}
                      strokeDashoffset="0"
                      strokeLinecap="round"
                    />
                    {/* Tick marks */}
                    {Array.from({ length: 6 }).map((_, i) => (
                      <line
                        key={i}
                        x1="50"
                        y1="45"
                        x2="50"
                        y2="50"
                        stroke="#d1d5db"
                        strokeWidth="1"
                        transform={`rotate(${i * 36 - 90} 50 50)`}
                      />
                    ))}
                  </svg>
                  <div className="absolute inset-x-0 bottom-0 flex flex-col items-center">
                    <span className="text-4xl font-bold">
                      {overallPerformance}%
                    </span>
                    <span className="text-xs text-gray-500">THIS TERM</span>
                  </div>
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center text-center p-6">
                  <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mb-4">
                    <ChartPie className="w-8 h-8 text-purple-600" />
                  </div>
                  <h3 className="text-lg font-medium mb-2">
                    No Performance Data
                  </h3>
                  <p className="text-sm text-gray-500">
                    Add departments to see the overall performance
                  </p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Bottom section with charts */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Performance Chart */}
        <Card>
          <CardContent className="p-6">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-lg font-medium">
                Performance Per Department
              </h2>
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-1">
                  <div className="w-2 h-2 rounded-full bg-purple-500"></div>
                  <span className="text-xs">Students</span>
                </div>
                <div className="flex items-center gap-1">
                  <div className="w-2 h-2 rounded-full bg-gray-300"></div>
                  <span className="text-xs">Staff</span>
                </div>
              </div>
            </div>

            {departments.length > 0 ? (
              <>
                <div className="h-64 flex items-end justify-between gap-2">
                  {departments.map(renderPerformanceChart)}
                </div>
                <div className="flex justify-between mt-2">
                  <span className="text-xs text-gray-500">0%</span>
                  <span className="text-xs text-gray-500">50%</span>
                  <span className="text-xs text-gray-500">100%</span>
                </div>
              </>
            ) : (
              <div className="flex flex-col items-center justify-center text-center h-64">
                <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mb-4">
                  <BarChart className="w-8 h-8 text-purple-600" />
                </div>
                <h3 className="text-lg font-medium mb-2">
                  No Performance Data
                </h3>
                <p className="text-sm text-gray-500">
                  Add departments to view performance metrics
                </p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Department Statistics */}
        <Card>
          <CardContent className="p-6">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-lg font-medium">Department Statistics</h2>
              <Button
                variant="outline"
                size="sm"
                className="flex items-center gap-1"
              >
                This Term
                <ChevronDown className="h-4 w-4" />
              </Button>
            </div>

            {departments.length > 0 ? (
              <div className="grid grid-cols-2 gap-6">
                {departments.slice(0, 4).map(renderDepartmentStats)}
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center text-center h-64">
                <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mb-4">
                  <Users className="w-8 h-8 text-purple-600" />
                </div>
                <h3 className="text-lg font-medium mb-2">
                  No Statistics Available
                </h3>
                <p className="text-sm text-gray-500">
                  Add departments to view statistics
                </p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      <DepartmentEditPanel
        department={selectedDepartment}
        isOpen={isDepartmentEditOpen}
        onClose={() => {
          setIsDepartmentEditOpen(false);
          setSelectedDepartment(null);
        }}
      />

      <AddDepartmentModal
        open={isAddDepartmentOpen}
        onOpenChange={setIsAddDepartmentOpen}
        sessionId={sessionId}
      />

      <AlertDialog
        open={!!departmentToDelete}
        onOpenChange={(open) => !open && setDepartmentToDelete(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you absolutely sure?</AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone. This will permanently delete the
              department "{departmentToDelete?.name}" and remove all associated
              data.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDeleteDepartment}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
